import { useCallback, useEffect, useState } from "react"

import { useCreateChatThread, useFetchChatThread, useFetchChatThreads } from "@/hooks/api/chat-threads"
import { ChatView } from "@/components/chat-threads/chat-view"

interface MeetingChatProps {
  meetingId: string
  meetingType?: string
}

export function MeetingChat({ meetingId, meetingType }: MeetingChatProps) {
  const [chatThreadId, setChatThreadId] = useState<string>("")
  const [isCreatingThread, setIsCreatingThread] = useState(false)

  const { data: chatThreads, isLoading: isLoadingThreads } = useFetchChatThreads({
    chat_threadable_type: "Meeting",
    chat_threadable_id: meetingId,
  })
  const chatThread = useFetchChatThread(chatThreadId)
  const { mutateAsync: createChatThread } = useCreateChatThread({
    chat_threadable_type: "Meeting",
    chat_threadable_id: meetingId,
  })

  useEffect(() => {
    if (!chatThreads || chatThreads.length === 0) return
    const currentStillExists = chatThreadId !== "" && chatThreads.some((t) => t.id === chatThreadId)
    if (!currentStillExists) {
      setChatThreadId(chatThreads[0].id)
    }
  }, [chatThreads, chatThreadId])

  const handleThreadSelect = useCallback((threadId: string) => {
    setChatThreadId(threadId)
  }, [])

  const handleCreateNewThread = useCallback(async () => {
    setIsCreatingThread(true)
    try {
      const newThread = await createChatThread({
        chat_threadable_type: "Meeting",
        chat_threadable_id: meetingId,
      })
      setChatThreadId(newThread.id)
    } finally {
      setIsCreatingThread(false)
    }
  }, [createChatThread, meetingId])

  useEffect(() => {
    if (!isLoadingThreads && chatThreads?.length === 0 && !isCreatingThread) {
      handleCreateNewThread()
    }
  }, [chatThreads?.length, handleCreateNewThread, isCreatingThread, isLoadingThreads])

  if (chatThreadId === "") return null

  return (
    <ChatView
      key={chatThreadId}
      threadId={chatThreadId}
      chatThread={chatThread}
      snippetChatType="Meeting"
      allThreads={chatThreads}
      onThreadSelect={handleThreadSelect}
      onCreateNewThread={handleCreateNewThread}
      isCreatingThread={isCreatingThread}
      placeholder="Ask anything about this meeting..."
      magicButtonContext={{ recordType: "meeting", recordId: meetingId }}
      hiddenPromptSnippetKeys={[
        meetingType === "prospect" ? "system.meeting.generate_pdf" : "system.meeting.create_discovery_analysis",
      ]}
    />
  )
}
